import React, { useEffect, useMemo, useRef } from 'react';
import WebsiteRenderer, { CanvasText } from './WebsiteRenderer';
import { materializePackage } from './sectionLibrary';
import { collectionRows, PRODUCT_KINDS } from './canvasModel';

export function nativeCollection(website, sectionId) {
  if (!isNativeWebsite(website)) return null;
  const pkg = materializePackage(website.package, website.edits);
  const section = pkg.pages.home.sections.find((s) => s.id === sectionId);
  const index = section?.root.children?.findIndex((n) => n.type === 'repeat');
  if (index == null || index < 0) return null;
  const node = section.root.children[index];
  const path = `home/${sectionId}/root/children/${index}`;
  const definition = pkg.bindings.collections[node.collection];
  const override = website.edits.blocks?.[path] || {};
  return { node, path, definition, override, changedKind: !!override.kind && override.kind !== definition.kind };
}

// A changed product type keeps this section's identity and uses the same live
// catalogue and configurators as an imported product grid.
function NativeProductGrid({ website, section, source, catalogue, onAction }) {
  const kind = source.override.kind;
  const rows = collectionRows(source.node, source.override, source.definition, website.edits, catalogue);
  return <section id={section.id} className="s6-section s6-surface-white" data-testid="native-product-grid" data-canvas-path={source.path}>
    <div className="s6-container"><h2 className="s6-h2 mb-6">{PRODUCT_KINDS[kind]}</h2>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{rows.map((product) => <article key={product.ref} className="s6-card p-5 flex flex-col gap-3">
        {product.image && <img src={product.image} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-xl" />}
        {product.image && product.native?.image_source === 'ai' && <span className="text-xs" data-testid="storefront-ai-image-tag">AI generated image</span>}
        <h3 className="s6-h3">{product.title}</h3><p className="text-sm line-clamp-3">{product.description}</p><strong>{product.price_label}</strong>
        <button type="button" className="s6-btn s6-btn-order mt-auto" disabled={!product.purchasable || !!website.canvas && !canEdit(website)} onClick={() => onAction('commerce.configure',product)}>Choose options</button>
        {!product.purchasable && <p className="text-sm">{product.availability_label || 'Currently unavailable'}</p>}
      </article>)}</div>
      {!rows.length && <p>No {PRODUCT_KINDS[kind]?.toLowerCase()} are available in this section right now.</p>}
    </div>
  </section>;
}

export const isNativeWebsite = (website) => !!website?.package?.native;
const send = (website,type,detail) => window.parent.postMessage({type,draftId:website.id,storeId:website.store_id,...detail},window.location.origin);
const canEdit = (website) => !!website?.canvas?.editMode && !!website.store_id && window.parent!==window;

export function nativeBrand(website, current) {
  if (!isNativeWebsite(website)) return current;
  const result=JSON.parse(JSON.stringify({...current,...website.package.native.brand}));
  Object.entries(website.edits.settings).forEach(([key,value]) => {
    const path=website.package.native.paths[key];
    if (!path) return;
    let target=result;
    path.slice(0,-1).forEach((p) => {target=target[p] ||= {};});
    target[path[path.length-1]]=website.package.manifest.settings[key]?.type==='image' ? website.package.assets[value] || '' : value;
  });
  if (website.edits.native?.faqs) result.faqs = website.edits.native.faqs;
  return result;
}

export const canvasChapter = (website) => canEdit(website) ? website.canvas.cinematicChapter || 'hello' : undefined;

export function nativeFieldPath(website,key) {
  if (!isNativeWebsite(website)) return null;
  const pkg=materializePackage(website.package,website.edits);
  for (const section of pkg.pages.home.sections) {
    const index=(section.root.children || []).findIndex((n) => n.attrs?.title===key);
    if(index>=0) return {page:'home',section:section.id,path:`home/${section.id}/root/children/${index}/children/0`};
  }
  return null;
}

export function nativeText(website,key,value) {
  if (!canEdit(website) || typeof value!=='string' || !value) return value;
  const spec=website.package.manifest.settings[key];
  const selected=nativeFieldPath(website,key);
  if (!selected || spec?.type!=='text') return value;
  return <CanvasText value={value} path={selected.path}
    onSelect={() => send(website,'PLATEOS_WEBSITE_CANVAS_SELECT',selected)}
    onEdit={(path,text) => send(website,'PLATEOS_WEBSITE_CANVAS_TEXT',{path,text})} />;
}

export function nativeProductRows(website,sectionId,rows,defaultRows=rows) {
  if (!isNativeWebsite(website)) return defaultRows;
  const source = nativeCollection(website,sectionId);
  if (!source) return defaultRows;
  const change=source.override;
  // Keep the theme's automatic photo strip until the merchant makes a choice.
  // Explicit selections and limits always use the full eligible catalogue.
  if (!Object.hasOwn(change,'ids') && !Object.hasOwn(change,'limit') && !website.edits.collections?.[source.node.collection]) return defaultRows;
  return collectionRows(source.node,change,source.definition,website.edits,{[source.definition.kind]:rows});
}

export function nativeSectionVisible(website,id) {
  if(!isNativeWebsite(website)) return true;
  const pkg=materializePackage(website.package,website.edits);
  return (website.edits.sections.home || pkg.pages.home.sections).some((s) => s.id===id && s.enabled);
}

export function nativeHeroDestination(website,fallback) {
  if (!isNativeWebsite(website)) return fallback;
  const pkg=materializePackage(website.package,website.edits);
  const section=pkg.pages.home.sections.find((s) => s.id==='hero');
  const i=section?.root.children.findIndex((n) => n.type==='action');
  const change=website.edits.blocks?.[`home/hero/root/children/${i}`];
  const original=website.package.pages.home.sections.find((s) => s.id==='hero')?.root.children[i];
  const action=section?.root.children[i];
  const target=change?.target || (action?.target!==original?.target ? action?.target : fallback);
  return target==='order' || target==='home' || nativeSectionVisible(website,target) ? target : 'home';
}

export default function NativeWebsiteRenderer({website,sections,plan,order=plan,catalogue,onAction}) {
  const pkg=useMemo(() => materializePackage(website.package,website.edits),[website.package,website.edits.layout]); // eslint-disable-line react-hooks/exhaustive-deps
  const root=useRef(null);
  const editing=canEdit(website);
  const sourceRows=pkg.pages.home.sections;
  const adaptiveRows=[...sourceRows].sort((a,b) => {const rank=(s) => s.id==='footer' ? 1000 : order.includes(s.id) ? order.indexOf(s.id) : 999;return rank(a)-rank(b);});
  const rows=website.edits.sections.home || (pkg.native.adaptive_order && !website.edits.layout?.pages?.home ? adaptiveRows : sourceRows);
  useEffect(() => {if(editing && !website.edits.sections.home) send(website,'PLATEOS_WEBSITE_NATIVE_ORDER',{ids:adaptiveRows.map((r) => r.id)});},[editing,website.id,JSON.stringify(adaptiveRows.map((r) => r.id)),website.edits.sections.home]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedSection=website.canvas?.selection?.section;
  const selectionRef=useRef(selectedSection);selectionRef.current=selectedSection;
  useEffect(() => {
    if(website.canvas?.focusToken && selectionRef.current) root.current?.querySelector(`[data-native-section="${CSS.escape(selectionRef.current)}"]`)?.scrollIntoView({block:'center'});
  },[website.canvas?.focusToken]);
  const select=(event,section) => {
    if(!editing) return;
    const target=event.target.nodeType===3 ? event.target.parentElement : event.target;
    let selected={page:'home',section:section.id,path:target.closest('[data-canvas-path]')?.dataset.canvasPath || `home/${section.id}/root`};
    if(target.tagName==='IMG') {
      const child=section.root.children.find((n) => n.children?.[0]?.type==='image' && (pkg.assets[website.edits.settings[n.attrs.title] ?? pkg.manifest.settings[n.attrs.title].default]===target.getAttribute('src')));
      if(child) selected=nativeFieldPath(website,child.attrs.title);
    }
    if(target.closest('button,a') && !target.closest('[contenteditable]')) {
      event.preventDefault();event.stopPropagation();
      if(section.id==='hero') {const index=section.root.children.findIndex((n) => n.type==='action'); if(index>=0) selected.path=`home/hero/root/children/${index}`;}
    }
    send(website,'PLATEOS_WEBSITE_CANVAS_SELECT',selected);
  };
  return <div ref={root} data-testid="native-website-canvas">
    {editing && <style>{`[data-native-section]:hover{outline:1px dashed #0d9488;outline-offset:-2px}[data-native-selected]{outline:2px solid #0d9488;outline-offset:-2px}[data-canvas-text]{cursor:text;white-space:pre-wrap}[data-canvas-text]:focus{outline:2px solid #0d9488}`}</style>}
    {rows.filter((s) => s.enabled).map((row) => {
      const section=pkg.pages.home.sections.find((s) => s.id===row.id);
      if(!section) return null;
      const builtIn=section.root.className===`plateos-native-${section.id}`;
      if(!builtIn) return <div key={row.id} id={row.id} data-native-section={row.id}><WebsiteRenderer website={{...website,package:{...pkg,native:undefined,pages:{home:{sections:[section]}}},edits:{...website.edits,layout:undefined,sections:{home:[row]}}}} catalogue={catalogue} onAction={onAction} disabled={!!website.canvas} onNavigate={(action,target) => {if(action==='navigate.page') window.scrollTo({top:0,behavior:'smooth'});else document.getElementById(target)?.scrollIntoView({behavior:'smooth'});}} /></div>;
      const source=nativeCollection(website,row.id);
      const switched=source?.changedKind;
      const available=switched || plan.includes(row.id);
      if(!available && (!editing || selectedSection!==row.id)) return null;
      return <div key={row.id} data-native-section={row.id} data-native-selected={editing && selectedSection===row.id ? '' : undefined} style={{...(row.id==='header' ? {display:'contents'} : {}),...website.edits.blocks?.[`home/${row.id}/root`]?.style}} onClickCapture={(e) => select(e,section)}>
        {switched ? <NativeProductGrid website={website} section={section} source={source} catalogue={catalogue} onAction={onAction} /> : available ? sections[row.id]?.() : <div style={{padding:24,background:'#f8fafc',color:'#64748b'}}> {section.label} · Add content or enable this section using its settings.</div>}
      </div>;
    })}
  </div>;
}
